import { isPlatformOnlySuperAdmin, normalizeRoles } from '../utils/platformRoles';
import { isConsultoriaTenant, isRequesterRole } from '../utils/tenantProfile';

const nav = (path, title, description) => ({
  element: `[data-tour="nav:${path}"]`,
  popover: { title, description, side: 'right', align: 'start' },
});

function welcome(title, description) {
  return { popover: { title, description, align: 'center' } };
}

const replay = {
  element: '[data-tour="account-menu"]',
  popover: {
    title: 'Rever quando quiser',
    description: 'Feche este roteiro quando quiser. Abra o menu da conta e escolha Ver tutorial para repetir do início.',
    side: 'bottom',
    align: 'end',
  },
};

const TOUR_IDS = {
  requerente: 'requerente',
  analista: 'orgao-analista',
  fiscal: 'orgao-fiscal',
  denuncias: 'orgao-denuncias',
  consulta: 'orgao-consulta',
  gestor: 'orgao-gestor',
  consultor: 'consultoria-consultor',
  coordenador: 'consultoria-coordenador',
  'gestor-consultoria': 'consultoria-gestor',
};

const ORGAO_MENU = [
  '/',
  '/me',
  '/assistant',
  '/notifications',
  '/process/routing',
  '/licenses',
  '/files',
  '/enterprises',
  '/process/cases',
  '/requerimentos',
  '/license/types',
  '/license/conditions',
  '/licenses/assisted',
  '/license/submodules',
  '/license/regularization-plans',
  '/legislation',
  '/portal',
  '/process/media-types',
  '/map',
  '/reports',
  '/complaint/complaints',
  '/complaint/categories',
  '/inspections',
  '/users',
  '/history',
  '/trash',
  '/profile',
  '/settings',
  '/catalog/company-sizes',
  '/catalog/company-statuses',
  '/catalog/economic-activities',
  '/contact/contacts',
  '/contact/positions',
  '/identity/persons',
  '/identity/companies',
  '/identity/clients',
  '/professional/technical-responsibles',
  '/professional/professional-councils',
  '/professional/professional-formations',
  '/institution/agencies',
  '/institution/departments',
  '/institution/job-titles',
  '/institution/staff',
  '/notifications/templates',
  '/notifications/send',
];

const CONSULTORIA_MENU = [
  '/',
  '/me',
  '/assistant',
  '/notifications',
  '/portfolio',
  '/process/cases',
  '/requerimentos',
  '/files',
  '/portal',
  '/licenses',
  '/enterprises',
  '/license/types',
  '/license/conditions',
  '/licenses/assisted',
  '/license/submodules',
  '/license/regularization-plans',
  '/legislation',
  '/map',
  '/reports',
  '/process/media-types',
  '/users',
  '/history',
  '/trash',
  '/profile',
  '/settings',
  '/catalog/company-sizes',
  '/catalog/company-statuses',
  '/catalog/economic-activities',
  '/contact/contacts',
  '/contact/positions',
  '/identity/persons',
  '/identity/companies',
  '/identity/clients',
  '/professional/technical-responsibles',
  '/professional/professional-councils',
  '/professional/professional-formations',
  '/notifications/templates',
  '/notifications/send',
];

function pick(menu, paths) {
  const allow = new Set(paths);
  return menu.filter((path) => allow.has(path));
}

const TOUR_PATHS = {
  requerente: pick(ORGAO_MENU, [
    '/assistant',
    '/notifications',
    '/licenses',
    '/files',
    '/enterprises',
    '/process/cases',
    '/requerimentos',
    '/license/conditions',
    '/license/submodules',
    '/license/regularization-plans',
    '/portal',
    '/map',
    '/identity/persons',
    '/identity/companies',
    '/identity/clients',
    '/profile',
  ]),
  analista: pick(ORGAO_MENU, [
    '/',
    '/me',
    '/assistant',
    '/notifications',
    '/process/routing',
    '/licenses',
    '/files',
    '/enterprises',
    '/process/cases',
    '/requerimentos',
    '/license/conditions',
    '/licenses/assisted',
    '/license/submodules',
    '/license/regularization-plans',
    '/legislation',
    '/portal',
    '/process/media-types',
    '/map',
    '/history',
    '/profile',
    '/catalog/company-sizes',
    '/catalog/company-statuses',
    '/catalog/economic-activities',
    '/contact/contacts',
    '/contact/positions',
    '/identity/persons',
    '/identity/companies',
    '/identity/clients',
  ]),
  fiscal: pick(ORGAO_MENU, [
    '/',
    '/me',
    '/assistant',
    '/notifications',
    '/licenses',
    '/files',
    '/portal',
    '/license/regularization-plans',
    '/map',
    '/complaint/complaints',
    '/complaint/categories',
    '/inspections',
    '/profile',
  ]),
  denuncias: pick(ORGAO_MENU, [
    '/',
    '/me',
    '/assistant',
    '/complaint/complaints',
    '/complaint/categories',
    '/inspections',
    '/profile',
  ]),
  consulta: pick(ORGAO_MENU, [
    '/',
    '/me',
    '/assistant',
    '/notifications',
    '/licenses',
    '/files',
    '/enterprises',
    '/portal',
    '/license/regularization-plans',
    '/map',
    '/reports',
    '/profile',
  ]),
  gestor: ORGAO_MENU,
  consultor: pick(CONSULTORIA_MENU, [
    '/',
    '/me',
    '/assistant',
    '/notifications',
    '/portfolio',
    '/process/cases',
    '/requerimentos',
    '/files',
    '/portal',
    '/licenses',
    '/enterprises',
    '/license/conditions',
    '/license/submodules',
    '/license/regularization-plans',
    '/map',
    '/process/media-types',
    '/history',
    '/profile',
    '/identity/persons',
    '/identity/companies',
    '/identity/clients',
  ]),
  coordenador: pick(CONSULTORIA_MENU, [
    '/',
    '/me',
    '/assistant',
    '/notifications',
    '/portfolio',
    '/process/cases',
    '/requerimentos',
    '/files',
    '/portal',
    '/licenses',
    '/enterprises',
    '/license/conditions',
    '/license/submodules',
    '/license/regularization-plans',
    '/map',
    '/reports',
    '/process/media-types',
    '/history',
    '/profile',
    '/identity/persons',
    '/identity/companies',
    '/identity/clients',
  ]),
  'gestor-consultoria': CONSULTORIA_MENU,
};

function voice(consultoria) {
  return consultoria
    ? { one: 'cliente', many: 'clientes', One: 'Cliente', place: 'consultoria' }
    : { one: 'requerente', many: 'requerentes', One: 'Requerente', place: 'órgão' };
}

function baseCopy(v) {
  return {
    '/': {
      title: 'Painel',
      description: `Indicadores da ${v.place}, filas e o que está pendente com os ${v.many}.`,
    },
    '/me': {
      title: 'Minhas tarefas',
      description: 'Pendências atribuídas a você: processo, prazo e o próximo passo.',
    },
    '/assistant': {
      title: 'Assistente',
      description: 'Pergunte em linguagem comum onde está um processo, uma licença ou um prazo.',
    },
    '/notifications': {
      title: 'Notificações',
      description: 'Alertas de prazo, tramitação e documentos que chegaram para você.',
    },
    '/process/routing': {
      title: 'Tramitação',
      description: `Caixa dos processos que estão com você ou com o seu setor, e o despacho para o próximo.`,
    },
    '/portfolio': {
      title: 'Carteira de clientes',
      description: 'Nome do cliente, licenças vinculadas e o próximo vencimento, com o prazo em dias ao lado da data.',
    },
    '/licenses': {
      title: 'Licenças',
      description: `Número, tipo, validade, situação e condicionantes da licença do ${v.one}.`,
    },
    '/files': {
      title: 'Arquivos',
      description: 'Anexos da licença e do processo: visualizar e baixar sem sair do módulo.',
    },
    '/enterprises': {
      title: 'Empreendimentos',
      description: `Empreendimento ligado à licença, com endereço e atividade do ${v.one}.`,
    },
    '/process/cases': {
      title: 'Processos',
      description: `Peças, despacho e o ${v.one} de cada processo.`,
    },
    '/requerimentos': {
      title: 'Requerimentos',
      description: `Pedidos protocolados pelo ${v.one} antes da licença ser emitida.`,
    },
    '/license/types': {
      title: 'Tipos de licença',
      description: 'Tipos usados na licença, como instalação, operação e regularização.',
    },
    '/license/conditions': {
      title: 'Condicionantes',
      description: 'Obrigações da licença, prazo de cumprimento e anexos de comprovação.',
    },
    '/licenses/assisted': {
      title: 'Licença assistida',
      description: 'Montagem da licença com apoio da leitura do processo e da minuta.',
    },
    '/license/submodules': {
      title: 'Submódulos da licença',
      description: 'Blocos extras do formulário de licença.',
    },
    '/license/regularization-plans': {
      title: 'Planos de regularização',
      description: 'Planos ligados à licença quando o empreendimento está em regularização.',
    },
    '/legislation': {
      title: 'Legislação',
      description: 'Normas usadas como base do parecer e da condicionante.',
    },
    '/portal': {
      title: v.one === 'cliente' ? 'Portal do cliente' : 'Portal do requerente',
      description: `Pendências, protocolo e documentos que o ${v.one} precisa responder.`,
    },
    '/process/media-types': {
      title: 'Tipos de mídia',
      description: 'Tipos de anexo aceitos no processo, como planta, foto e estudo.',
    },
    '/map': {
      title: 'Mapa',
      description: 'Local do empreendimento e o ponto da ocorrência ou da vistoria.',
    },
    '/reports': {
      title: 'Relatórios',
      description: `Prazos, volumes e a carga da ${v.place} para acompanhamento.`,
    },
    '/complaint/complaints': {
      title: 'Denúncias',
      description: 'Relato, status do atendimento e o encaminhamento da ocorrência.',
    },
    '/complaint/categories': {
      title: 'Categorias de denúncia',
      description: 'Tipos de ocorrência usados na triagem, antes de enviar a campo.',
    },
    '/inspections': {
      title: 'Vistoria',
      description: 'Checklist da visita e o auto, quando a fiscalização termina no local.',
    },
    '/users': {
      title: 'Usuários',
      description: `Contas da equipe e o papel de cada pessoa na ${v.place}.`,
    },
    '/history': {
      title: 'Histórico',
      description: 'Quem alterou cadastro, licença ou processo, e quando.',
    },
    '/trash': {
      title: 'Lixeira',
      description: 'Registros excluídos que ainda podem ser restaurados.',
    },
    '/profile': {
      title: 'Meu perfil',
      description: 'Seus dados de acesso e a forma como o sistema mostra o seu nome.',
    },
    '/settings': {
      title: 'Configurações',
      description: `Dados da ${v.place}, aparência e preferências da organização.`,
    },
    '/catalog/company-sizes': {
      title: 'Portes de empresa',
      description: 'Portes usados no cadastro da empresa, como microempresa e médio porte.',
    },
    '/catalog/company-statuses': {
      title: 'Status de empresa',
      description: 'Situações cadastrais da empresa, como ativa ou baixada.',
    },
    '/catalog/economic-activities': {
      title: 'Atividades econômicas',
      description: 'CNAE e atividades usadas no empreendimento e na licença.',
    },
    '/contact/contacts': {
      title: 'Contatos',
      description: `Telefone e e-mail de quem representa o ${v.one}.`,
    },
    '/contact/positions': {
      title: 'Cargos de contato',
      description: 'Funções do contato, como responsável legal ou técnico.',
    },
    '/identity/persons': {
      title: 'Pessoas',
      description: `Pessoa física ligada ao ${v.one}, com documento e contato.`,
    },
    '/identity/companies': {
      title: 'Empresas',
      description: `Cadastro da empresa do ${v.one}, com CNPJ e endereço da sede.`,
    },
    '/identity/clients': {
      title: v.One === 'Cliente' ? 'Clientes' : 'Requerentes',
      description: `${v.One} acompanhado pela ${v.place}, ligado à empresa ou à pessoa.`,
    },
    '/professional/technical-responsibles': {
      title: 'Responsáveis técnicos',
      description: 'Profissionais que assinam estudo, projeto ou a licença.',
    },
    '/professional/professional-councils': {
      title: 'Conselhos profissionais',
      description: 'Conselhos de classe usados no registro do responsável técnico.',
    },
    '/professional/professional-formations': {
      title: 'Formações',
      description: 'Formação do responsável técnico, como engenharia ou biologia.',
    },
    '/institution/agencies': {
      title: 'Órgãos',
      description: 'Órgão ambiental e as unidades que emitem ou analisam.',
    },
    '/institution/departments': {
      title: 'Setores',
      description: 'Setores da tramitação, para saber com quem o processo está.',
    },
    '/institution/job-titles': {
      title: 'Cargos',
      description: 'Cargos dos servidores usados no despacho e na assinatura.',
    },
    '/institution/staff': {
      title: 'Servidores',
      description: 'Servidores do órgão e o setor em que atuam.',
    },
    '/notifications/templates': {
      title: 'Modelos de notificação',
      description: 'Textos prontos de e-mail e mensagem para prazo, exigência e licença.',
    },
    '/notifications/send': {
      title: 'Enviar notificação',
      description: `Disparo manual para o ${v.one} ou para a equipe, pelo canal escolhido.`,
    },
  };
}

const WELCOME = {
  requerente: {
    title: 'Portal do requerente',
    description:
      'O roteiro percorre o que este acesso libera: pendências, empreendimento, licenças e documentos. A licença nova continua com o órgão. Dá para fechar e repetir em Ver tutorial.',
  },
  analista: {
    title: 'Fila do analista',
    description:
      'O roteiro passa pela tramitação, pelo processo, pela licença e pelos cadastros que entram no parecer. Itens fora do seu acesso não aparecem.',
  },
  fiscal: {
    title: 'Fiscalização em campo',
    description:
      'O roteiro mostra mapa, denúncia, licença do local e a vistoria. Use estes atalhos quando sair para o empreendimento.',
  },
  denuncias: {
    title: 'Atendimento de denúncias',
    description:
      'O roteiro cobre o registro, a categoria e a vistoria ligada à ocorrência. O restante do licenciamento fica com os outros perfis.',
  },
  consulta: {
    title: 'Consulta',
    description:
      'Este acesso é de leitura. O roteiro mostra licenças, empreendimentos, mapa e relatórios, sem tramitar nem alterar cadastro.',
  },
  gestor: {
    title: 'Gestão do órgão',
    description:
      'O roteiro percorre o menu inteiro da gestão: painel, tramitação, licenças, campo, cadastros, equipe e notificações. O que o seu papel não abre é pulado.',
  },
  consultor: {
    title: 'Fila da consultoria',
    description:
      'O roteiro mostra tarefas, carteira, processos, licenças e empreendimentos dos clientes. O que não é do consultor fica de fora.',
  },
  coordenador: {
    title: 'Coordenação da carteira',
    description:
      'O roteiro percorre a carteira, os prazos, as licenças e os relatórios dos clientes. O nome exibido é o do cliente, não o identificador.',
  },
  'gestor-consultoria': {
    title: 'Gestão da consultoria',
    description:
      'O roteiro cobre painel, carteira, licenças, cadastros e a equipe. O que o seu papel não abre é pulado.',
  },
};

const ROLE_COPY = {
  requerente: {
    '/licenses': 'Veja as licenças já emitidas. Incluir uma licença nova fica com a equipe do órgão.',
    '/enterprises': 'Cadastre o empreendimento do pedido e confira endereço e atividade.',
    '/process/cases': 'Acompanhe o processo do seu protocolo, sem tramitar no lugar do órgão.',
    '/portal': 'Exigências, protocolos e documentos que o órgão pediu para você responder.',
    '/license/conditions': 'Condicionantes da sua licença e o que ainda precisa ser comprovado.',
  },
  analista: {
    '/process/routing': 'Comece por aqui: processos com você ou com o seu setor, e o despacho seguinte.',
    '/process/cases': 'Abra o processo para ler peças, despacho e o requerente antes do parecer.',
    '/licenses': 'Confira tipo, validade e condicionantes antes de concluir a análise.',
    '/licenses/assisted': 'Use a licença assistida para montar a minuta a partir do processo.',
    '/me': 'O que foi atribuído a você na análise aparece nesta fila.',
  },
  fiscal: {
    '/map': 'Localize o empreendimento e o ponto da ocorrência antes de sair.',
    '/complaint/complaints': 'Leia o relato que originou a ida a campo.',
    '/licenses': 'Confira a licença do local: validade e condicionantes que a vistoria vai checar.',
    '/inspections': 'Registre o checklist e o auto quando a visita terminar.',
  },
  denuncias: {
    '/complaint/complaints': 'Abra o registro para atualizar o atendimento, o status e o encaminhamento.',
    '/complaint/categories': 'Classifique a ocorrência antes de enviar para a fiscalização.',
    '/inspections': 'Acompanhe a vistoria quando a denúncia for a campo.',
  },
  consulta: {
    '/licenses': 'Consulte número, validade e situação. Este perfil não edita a licença.',
    '/enterprises': 'Consulte o empreendimento ligado à licença, sem alterar o cadastro.',
    '/reports': 'Relatórios de prazo e volume, só para leitura.',
    '/map': 'Veja o ponto do empreendimento sem registrar vistoria.',
  },
  gestor: {
    '/': 'Painel da gestão: filas paradas, prazos e o que precisa de decisão.',
    '/process/routing': 'Acompanhe o que está parado em cada setor e redistribua se precisar.',
    '/users': 'Conceda papel de analista, fiscal, denúncias ou consulta para a equipe.',
    '/licenses': 'Visão das licenças do órgão, com validade e condicionantes.',
    '/institution/departments': 'Setores que recebem processo na tramitação.',
    '/settings': 'Dados do órgão, identidade visual e preferências da organização.',
  },
  consultor: {
    '/me': 'Processos e pendências dos clientes que estão com você.',
    '/portfolio': 'Seus clientes, as licenças de cada um e o próximo vencimento.',
    '/licenses': 'Validade, condicionantes e o que o cliente ainda precisa entregar.',
    '/enterprises': 'Empreendimento do cliente ligado à licença.',
    '/process/cases': 'Processo do cliente: peças, despacho e o que falta protocolar.',
  },
  coordenador: {
    '/portfolio': 'Carteira inteira: nome do cliente, licenças e qual prazo vem primeiro.',
    '/licenses': 'Abra a licença do cliente para revisar condicionantes e publicação.',
    '/reports': 'Carga da equipe e os prazos da carteira.',
    '/process/cases': 'Processos dos clientes sob a sua coordenação.',
  },
  'gestor-consultoria': {
    '/': 'Pendências da consultoria e o volume de licenças dos clientes.',
    '/portfolio': 'Quem está sob gestão e qual licença vence primeiro.',
    '/users': 'Inclua consultores e coordenadores e defina o papel de cada um.',
    '/licenses': 'Emissão, validade e condicionantes dos clientes.',
    '/settings': 'Dados da consultoria e a identidade visual usada com o cliente.',
  },
};

function buildSteps(tourKey, consultoria) {
  const v = voice(consultoria);
  const base = baseCopy(v);
  const overlay = ROLE_COPY[tourKey] || {};
  const intro = WELCOME[tourKey];
  const paths = TOUR_PATHS[tourKey] || [];
  return [
    welcome(intro.title, intro.description),
    ...paths.map((path) => {
      const item = base[path];
      return nav(path, item.title, overlay[path] || item.description);
    }),
    replay,
  ];
}

const STAFF_ROLES = [
  'analista',
  'fiscal',
  'denuncias',
  'consulta',
  'consultor',
  'coordenador',
  'gestor_ambiental',
  'tenant_admin',
  'super_admin',
];

export function resolveTourId(user, tenant) {
  if (!user || isPlatformOnlySuperAdmin(user)) return null;
  const roles = normalizeRoles(user);
  const consultoria = isConsultoriaTenant(tenant);
  const staff = roles.some((role) => STAFF_ROLES.includes(role));
  if (!staff && isRequesterRole(user)) return 'requerente';
  if (roles.includes('gestor_ambiental') || roles.includes('tenant_admin') || roles.includes('super_admin')) {
    return consultoria ? 'gestor-consultoria' : 'gestor';
  }
  if (consultoria && roles.includes('coordenador')) return 'coordenador';
  if (roles.includes('denuncias')) return 'denuncias';
  if (roles.includes('fiscal')) return 'fiscal';
  if (roles.includes('consultor') || (consultoria && roles.includes('analista'))) return 'consultor';
  if (roles.includes('analista')) return 'analista';
  if (roles.includes('consulta')) return 'consulta';
  if (isRequesterRole(user)) return 'requerente';
  return consultoria ? 'consultor' : 'analista';
}

export function tourFor(user, tenant) {
  const key = resolveTourId(user, tenant);
  if (!key) return null;
  return {
    id: TOUR_IDS[key],
    steps: buildSteps(key, isConsultoriaTenant(tenant)),
  };
}

export function tourSeenKey(user, tourId) {
  const who = user?.id || user?.email || user?.username || 'anon';
  return `useambiental.tour.seen.v2.${who}.${tourId}`;
}
